import {
	ChevronLeft,
	ChevronRight,
	Download,
	Eye,
	FolderOpen,
	Loader2,
	MoreHorizontal,
	Pencil,
	Trash2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import type { DocumentCategory, DocumentDisposition } from "@/adapters/api";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useDocumentFileURL } from "../../hooks/use-document-file-url.ts";
import {
	DOCUMENTS_PAGE_SIZE,
	type DocumentsFilters,
	useDocuments,
} from "../../hooks/use-documents.ts";
import { useRemoveDocument } from "../../hooks/use-remove-document.ts";
import type { ConstructionDocument } from "../../types.ts";
import { documentCategoryLabel } from "../../utils/categories.ts";
import { documentErrorMessage } from "../../utils/errors.ts";
import { formatDocumentDate } from "../../utils/format-date.ts";
import { formatFileSize } from "../../utils/format-size.ts";
import { CategoryFilter } from "../category-filter";
import { DocumentCategoryBadge } from "../document-category-badge";
import { DocumentFileIcon } from "../document-file-icon";
import { EditDocumentDialog } from "../edit-document-dialog";

export interface DocumentsListPermissions {
	canView: boolean;
	canUpdate: boolean;
	canDelete: boolean;
}

interface DocumentsListProps extends DocumentsListPermissions {
	constructionId: string;
}

export function DocumentsListSkeleton() {
	return (
		<Card className="documents-list__card">
			<div className="documents-list__skeleton">
				{Array.from({ length: 4 }, (_, index) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: static placeholder list
					<Skeleton key={index} className="documents-list__skeleton-row" />
				))}
			</div>
		</Card>
	);
}

interface DocumentRowProps extends DocumentsListPermissions {
	document: ConstructionDocument;
	opening: DocumentDisposition | undefined;
	onOpen: (
		document: ConstructionDocument,
		disposition: DocumentDisposition,
	) => void;
	onEdit: (document: ConstructionDocument) => void;
	onRemove: (document: ConstructionDocument) => void;
}

function DocumentRow({
	document,
	opening,
	canView,
	canUpdate,
	canDelete,
	onOpen,
	onEdit,
	onRemove,
}: DocumentRowProps) {
	return (
		<li className="documents-list__item" data-testid="document-row">
			<DocumentFileIcon fileName={document.fileName} />
			<div className="documents-list__main">
				<div className="documents-list__title-row">
					<span className="documents-list__title" title={document.title}>
						{document.title}
					</span>
					{canUpdate && document.clientVisible && (
						<Badge variant="info" className="documents-list__client-badge">
							Cliente
						</Badge>
					)}
				</div>
				<div className="documents-list__meta">
					<DocumentCategoryBadge category={document.category} />
					<span>{formatFileSize(document.sizeBytes)}</span>
					{document.uploadedBy.name && <span>{document.uploadedBy.name}</span>}
					<span>{formatDocumentDate(document.createdAt)}</span>
				</div>
			</div>
			<div className="documents-list__actions">
				{canView && (
					<>
						<Button
							variant="outline"
							size="sm"
							className="documents-list__action"
							disabled={!!opening}
							aria-label={`Visualizar ${document.title}`}
							onClick={() => onOpen(document, "inline")}
						>
							{opening === "inline" ? (
								<Loader2 className="documents-list__spinner" />
							) : (
								<Eye />
							)}
							<span className="documents-list__action-label">Visualizar</span>
						</Button>
						<Button
							variant="outline"
							size="sm"
							className="documents-list__action"
							disabled={!!opening}
							aria-label={`Baixar ${document.title}`}
							onClick={() => onOpen(document, "attachment")}
						>
							{opening === "attachment" ? (
								<Loader2 className="documents-list__spinner" />
							) : (
								<Download />
							)}
							<span className="documents-list__action-label">Baixar</span>
						</Button>
					</>
				)}
				{(canUpdate || canDelete) && (
					<DropdownMenu modal={false}>
						<DropdownMenuTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								aria-label={`Mais ações de ${document.title}`}
							>
								<MoreHorizontal />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							{canUpdate && (
								<DropdownMenuItem onClick={() => onEdit(document)}>
									<Pencil />
									Editar
								</DropdownMenuItem>
							)}
							{canUpdate && canDelete && <DropdownMenuSeparator />}
							{canDelete && (
								<DropdownMenuItem
									variant="destructive"
									onClick={() => onRemove(document)}
								>
									<Trash2 />
									Remover
								</DropdownMenuItem>
							)}
						</DropdownMenuContent>
					</DropdownMenu>
				)}
			</div>
		</li>
	);
}

function emptyMessage(category: DocumentCategory | undefined) {
	return category
		? `Nenhum documento em ${documentCategoryLabel(category)}.`
		: "Nenhum documento enviado para esta obra ainda.";
}

export function DocumentsList({
	constructionId,
	canView,
	canUpdate,
	canDelete,
}: DocumentsListProps) {
	const [filters, setFilters] = useState<DocumentsFilters>({ page: 1 });
	const [editing, setEditing] = useState<ConstructionDocument>();
	const [removing, setRemoving] = useState<ConstructionDocument>();
	const [opening, setOpening] = useState<{
		id: string;
		disposition: DocumentDisposition;
	}>();
	const query = useDocuments(constructionId, filters);
	const fileURL = useDocumentFileURL(constructionId);
	const removeDocument = useRemoveDocument(constructionId);

	const page = filters.page ?? 1;
	const count = query.data?.count ?? 0;
	const pageCount = Math.max(1, Math.ceil(count / DOCUMENTS_PAGE_SIZE));

	async function open(
		document: ConstructionDocument,
		disposition: DocumentDisposition,
	) {
		setOpening({ id: document.id, disposition });
		try {
			await fileURL.mutateAsync({ documentId: document.id, disposition });
		} catch (err) {
			toast.error(
				documentErrorMessage(err, "Não foi possível abrir o documento."),
			);
		} finally {
			setOpening(undefined);
		}
	}

	async function confirmRemove() {
		if (!removing) return;
		try {
			await removeDocument.mutateAsync(removing.id);
			toast.success("Documento removido.");
			setRemoving(undefined);
		} catch (err) {
			toast.error(
				documentErrorMessage(err, "Não foi possível remover o documento."),
			);
		}
	}

	function body() {
		if (query.isPending) return <DocumentsListSkeleton />;
		if (query.isError) {
			return (
				<Card>
					<div className="card-state">
						<p>Não foi possível carregar os documentos.</p>
						<Button variant="outline" onClick={() => query.refetch()}>
							Tentar novamente
						</Button>
					</div>
				</Card>
			);
		}
		const items = query.data.items;
		if (items.length === 0) {
			return (
				<Card>
					<div className="card-state documents-list__empty">
						<FolderOpen />
						<p>{emptyMessage(filters.category)}</p>
					</div>
				</Card>
			);
		}
		return (
			<Card className="documents-list__card">
				<ul className="documents-list__items" aria-label="Documentos">
					{items.map((document) => (
						<DocumentRow
							key={document.id}
							document={document}
							opening={
								opening?.id === document.id ? opening.disposition : undefined
							}
							canView={canView}
							canUpdate={canUpdate}
							canDelete={canDelete}
							onOpen={open}
							onEdit={setEditing}
							onRemove={setRemoving}
						/>
					))}
				</ul>
				{pageCount > 1 && (
					<div className="documents-list__pagination">
						<span className="documents-list__pagination-label">
							Página {page} de {pageCount}
						</span>
						<div className="documents-list__pagination-actions">
							<Button
								variant="outline"
								size="sm"
								disabled={page <= 1}
								onClick={() =>
									setFilters((current) => ({ ...current, page: page - 1 }))
								}
							>
								<ChevronLeft /> Anterior
							</Button>
							<Button
								variant="outline"
								size="sm"
								disabled={page >= pageCount}
								onClick={() =>
									setFilters((current) => ({ ...current, page: page + 1 }))
								}
							>
								Próxima <ChevronRight />
							</Button>
						</div>
					</div>
				)}
			</Card>
		);
	}

	return (
		<div className="documents-list">
			<CategoryFilter
				value={filters.category}
				counts={query.data?.countsByCategory}
				onChange={(category) => setFilters({ category, page: 1 })}
			/>

			{body()}

			<EditDocumentDialog
				constructionId={constructionId}
				document={editing}
				onOpenChange={(next) => {
					if (!next) setEditing(undefined);
				}}
			/>
			<ConfirmDialog
				open={!!removing}
				onOpenChange={(next) => {
					if (!next) setRemoving(undefined);
				}}
				title="Remover documento?"
				description={`"${removing?.title ?? ""}" deixará de aparecer para toda a equipe da obra.`}
				confirmLabel="Remover"
				destructive
				isPending={removeDocument.isPending}
				onConfirm={confirmRemove}
			/>
		</div>
	);
}
